import type { ProductFnbDto } from '@telestore/contracts';
import { pickText } from '@telestore/contracts';
import { formatMoney } from '@telestore/shared';
import type { Currency } from '@telestore/shared';
import type { Locale, TranslationKey, TranslationParams } from '@telestore/i18n';
import { Check } from 'lucide-react';

/**
 * F&B modifier picker (§4: `modifier_group` with required-pick-1 / pick-0..N).
 *
 * The rules live in `@telestore/catalog`'s `resolveModifiers`, which the server
 * runs again at checkout. This component's job is only to make the legal picks
 * obvious and the illegal ones unreachable — it never decides a price, and a
 * customer who defeats it still gets a correct order or a clear rejection.
 *
 * Unavailable options stay visible and struck through for the same reason
 * out-of-stock sizes do on a retail product: hiding them makes a customer
 * think the shop simply does not serve it, and they message to ask.
 */

export type ModifierGroup = ProductFnbDto['modifierGroups'][number];

export interface ModifierPickerProps {
  groups: readonly ModifierGroup[];
  selection: Record<string, string[]>;
  onChange: (selection: Record<string, string[]>) => void;
  currency: Currency;
  locale: Locale;
  t: (key: TranslationKey, params?: TranslationParams) => string;
}

/**
 * Which required groups still need a pick.
 *
 * Exported because the add-to-cart button needs the same answer, and two
 * copies of "is this order complete" is how a disabled button and a rejected
 * checkout end up disagreeing.
 */
export function missingRequired(
  groups: readonly ModifierGroup[],
  selection: Record<string, string[]>,
): ModifierGroup[] {
  return groups.filter((group) => (selection[group.id]?.length ?? 0) < group.minSelect);
}

/** Sum of the chosen options' deltas — display only; the server re-prices. */
export function modifierDelta(
  groups: readonly ModifierGroup[],
  selection: Record<string, string[]>,
): number {
  let total = 0;
  for (const group of groups) {
    for (const optionId of selection[group.id] ?? []) {
      const option = group.options.find((entry) => entry.id === optionId);
      if (option) total += option.priceDelta;
    }
  }
  return total;
}

/**
 * Pre-select every group's defaults.
 *
 * Run once when a product loads rather than lazily at checkout: a customer
 * should see "Regular sugar" already chosen and the price it implies, not
 * discover a default they never agreed to on the payment screen.
 */
export function defaultSelection(groups: readonly ModifierGroup[]): Record<string, string[]> {
  const selection: Record<string, string[]> = {};
  for (const group of groups) {
    const defaults = group.options
      .filter((option) => option.isDefault && option.isAvailable)
      .slice(0, Math.max(group.maxSelect, 1))
      .map((option) => option.id);
    if (defaults.length > 0) selection[group.id] = defaults;
  }
  return selection;
}

export function ModifierPicker({
  groups,
  selection,
  onChange,
  currency,
  locale,
  t,
}: ModifierPickerProps) {
  if (groups.length === 0) return null;

  function toggle(group: ModifierGroup, optionId: string) {
    const current = selection[group.id] ?? [];
    const chosen = current.includes(optionId);

    if (group.selection === 'single') {
      /**
       * Tapping the chosen option again clears it only when the group is
       * optional. On a required group that would leave the customer stuck
       * with a picker that looks unanswered and no way back.
       */
      if (chosen && group.minSelect === 0) {
        const next = { ...selection };
        delete next[group.id];
        onChange(next);
        return;
      }
      onChange({ ...selection, [group.id]: [optionId] });
      return;
    }

    if (chosen) {
      onChange({ ...selection, [group.id]: current.filter((id) => id !== optionId) });
      return;
    }
    // At the cap, ignore the tap rather than silently dropping an earlier pick.
    if (current.length >= group.maxSelect) return;
    onChange({ ...selection, [group.id]: [...current, optionId] });
  }

  return (
    <div className="flex flex-col gap-5">
      {groups.map((group) => {
        const current = selection[group.id] ?? [];
        const required = group.minSelect > 0;
        const atCap = group.selection === 'multiple' && current.length >= group.maxSelect;

        return (
          <fieldset key={group.id}>
            <legend className="mb-2 flex w-full items-baseline justify-between gap-2">
              <span className="text-sm font-medium">{pickText(group.name, locale)}</span>
              <span className="text-xs opacity-60">
                {required
                  ? t('storefront.modifierRequired')
                  : group.selection === 'multiple'
                    ? t('storefront.modifierUpTo', { count: group.maxSelect })
                    : t('storefront.modifierOptional')}
              </span>
            </legend>

            <div className="flex flex-col gap-1.5">
              {group.options.map((option) => {
                const chosen = current.includes(option.id);
                // A capped group locks its unchosen options, not its chosen ones.
                const locked = !option.isAvailable || (atCap && !chosen);

                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={locked}
                    onClick={() => toggle(group, option.id)}
                    className="flex items-center justify-between gap-3 rounded-[var(--radius)] px-3 py-2.5 text-left text-sm"
                    style={{
                      border: `1px solid ${chosen ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      background: chosen ? 'color-mix(in srgb, var(--color-primary) 8%, transparent)' : 'transparent',
                      opacity: locked ? 0.4 : 1,
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="flex size-[18px] shrink-0 items-center justify-center"
                        style={{
                          border: `1px solid ${chosen ? 'var(--color-primary)' : 'var(--color-border)'}`,
                          background: chosen ? 'var(--color-primary)' : 'transparent',
                          color: 'var(--color-primary-contrast)',
                          // Radio for pick-one, checkbox for pick-many: the
                          // shape tells the customer the rule before they tap.
                          borderRadius: group.selection === 'single' ? '999px' : '4px',
                        }}
                      >
                        {chosen ? <Check size={12} strokeWidth={3} /> : null}
                      </span>
                      <span
                        style={{ textDecoration: option.isAvailable ? 'none' : 'line-through' }}
                      >
                        {pickText(option.name, locale)}
                      </span>
                    </span>

                    {option.priceDelta !== 0 ? (
                      <span className="shrink-0 text-xs tabular-nums opacity-70">
                        {option.priceDelta > 0 ? '+' : '−'}
                        {formatMoney({ amount: Math.abs(option.priceDelta), currency })}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
